import type { EditableCxRumEvent, NetworkRequestContext } from "@coralogix/browser";

// Schemes that are never our code: browser extensions, plus the masked URL Safari
// 15.4+ substitutes for them. `URL.origin` is "null" for all of these, so the
// http(s) comparison below cannot classify them.
const nonHttpThirdPartyScheme =
  /^(?:chrome-extension|moz-extension|safari-web-extension|safari-extension|ms-browser-extension|webkit-masked-url):\/\//i;

/**
 * True when a stack-frame `fileName` is served by someone else — extensions, reCAPTCHA,
 * GTM, `attributer.js`, Pardot. Compared by origin rather than by host list, which also
 * catches Pardot on `x.coro.net` (our domain, their code). Fails open: a frame we cannot
 * classify is never treated as third party.
 * @param fileName - `fileName` of a stack frame.
 */
const isThirdPartyFileName = (fileName: string): boolean => {
  if (nonHttpThirdPartyScheme.test(fileName)) {
    return true;
  }
  const appOrigin = window.location.origin;
  if (!appOrigin) {
    return false;
  }
  try {
    const { origin, protocol } = new URL(fileName, appOrigin);
    return /^https?:$/.test(protocol) && origin !== appOrigin;
  } catch {
    return false;
  }
};

/**
 * True when the throw site — the top frame carrying a fileName — is third-party code.
 * Throw site only, never `some(frame)`: our own error passing through a third-party
 * callback is still our bug. Known cost: if we call a third-party SDK badly and it
 * throws, the throw site is theirs and we drop our own bug.
 * @param event - RUM error event.
 */
export const isThirdPartyError = (event: EditableCxRumEvent): boolean => {
  const fileName = (event.error_context?.original_stacktrace ?? []).find(
    (frame) => frame.fileName
  )?.fileName;
  return !!fileName && isThirdPartyFileName(fileName);
};

/**
 * True for network failures from our own API that the app handles by design: any 401
 * (retried by the interceptor), the no-session `/logout` 400, and the `/msp/hierarchy`
 * 403 that `checkMspAccess()` provokes to ask whether the user is an MSP (PLAT-4382).
 *
 * Dropping these means an authorization regression on those endpoints is not a RUM error
 * event — track it with a metric instead. `/msp/hierarchy` also has a second caller,
 * `msp.module.getRelevantHierarchy()`, whose 403 IS an incident; it stays visible through
 * that caller's own console error, which this filter never sees.
 * @param request - Network request context of the RUM event.
 * @param apiHost - Our own API host (`VITE_API_HOST`).
 */
export const isExpectedApiError = (
  request: NetworkRequestContext | undefined,
  apiHost: string | undefined
): boolean => {
  const status = request?.status_code;
  if (status !== 401 && status !== 400 && status !== 403) {
    return false;
  }
  if (!apiHost || !request?.url?.startsWith(apiHost)) {
    return false;
  }
  // Path only, so adding a query param to either request cannot silently disable this.
  const [path] = request.url.split("?");
  return (
    status === 401 ||
    (status === 400 && path.endsWith("/logout")) ||
    (status === 403 && path.endsWith("/msp/hierarchy"))
  );
};
